import { useMutation } from '@apollo/client'
import { Drawer, Input, message, Radio } from 'antd'
import classNames from 'classnames'
import { useUpdateAtom } from 'jotai/utils'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyForm } from '~/components/ui/MyForm'
import { MyIcon } from '~/components/ui/MyIcon'
import { CHECK_REDEEM_CODE, CREATE_DANLU_BY_CODE } from '~/graphqls/gqls/danlu'
import {
  checkRedeemCode,
  checkRedeemCodeVariables,
  createDanluByCode,
  createDanluByCodeVariables,
  SystemCode,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { graphQLErrorMessage } from '~/utils'
import { scheduleListUpdateAtom } from './useAtom'
import styles from './CreateDrawer.module.css'
import { memo, useRef, useState } from 'react'
import { useRouter } from 'next/router'

interface Props {
  visible: boolean
  onClose: () => void
}

function RedeemCodeDrawer({ visible, onClose }: Props) {
  const [form] = MyForm.useForm()
  const selectedImage = MyForm.useWatch('image', form)
  const scheduleListUpdate = useUpdateAtom(scheduleListUpdateAtom)
  const submitLock = useRef(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const router = useRouter()

  const checkRedeemCode = useImperativeQuery<
    checkRedeemCode,
    checkRedeemCodeVariables
  >(CHECK_REDEEM_CODE)
  const [createDanluByCode] = useMutation<
    createDanluByCode,
    createDanluByCodeVariables
  >(CREATE_DANLU_BY_CODE)

  const handleSubmit = async () => {
    let formData
    try {
      formData = await form.validateFields()
    } catch (err: any) {
      throw err
    }
    try {
      if (submitLock.current) return
      submitLock.current = true
      setSubmitLoading(true)
      await checkRedeemCode(
        { code: formData.code },
        {
          headers: {
            system_code: SystemCode.WUJIE_AI,
          },
        }
      )
      const res = await createDanluByCode({
        variables: {
          input: {
            conversionCode: formData.code,
            name: formData.name.trim(),
          },
        },
      })

      scheduleListUpdate(true)
      onClose()
      if (router.pathname !== '/pro/danlu') {
        router.push('/pro/danlu')
      }
    } catch (err) {
      message.destroy()
      message.error(graphQLErrorMessage(err))
    } finally {
      submitLock.current = false
      setSubmitLoading(false)
    }
  }

  return (
    <Drawer
      width={584}
      open={visible}
      onClose={onClose}
      closable={false}
      bodyStyle={{ padding: 0 }}
    >
      <section className="w-full h-full relative pb-[80px] pt-4 px-6 flex-shrink-0">
        <div className="flex items-center text-xl justify-between text-neutral-800 mb-6">
          <span>使用兑换码新建</span>
          <span
            className="cursor-pointer w-10 h-10 rounded-full border-2 border-solid border-neutral-300 hover:border-neutral-400 flex items-center transition-all"
            onClick={onClose}
          >
            <MyIcon
              icon="close"
              className="text-neutral-400 mx-auto hover:text-neutral-500 transition-all"
            />
          </span>
        </div>
        <div
          className="overflow-y-auto scrollbar-hidden"
          style={{ height: 'calc(100vh - 148px)' }}
        >
          <MyForm
            layout="vertical"
            $dark
            form={form}
            initialValues={{
              image: 1,
            }}
          >
            <div className="mb-6">
              <div className={styles.label}>兑换码</div>
              <MyForm.Item
                name="code"
                rules={[
                  { required: true, message: '请输入兑换码' },
                  { validateTrigger: ['onChange'] },
                ]}
                normalize={v => v.trim()}
              >
                <Input placeholder="请输入兑换码" showCount maxLength={20} />
              </MyForm.Item>
            </div>
            <div className="mb-6">
              <div className={styles.label}>丹炉名称</div>
              <MyForm.Item
                name="name"
                rules={[
                  { required: true, message: '请输入丹炉名称' },
                  { validateTrigger: ['onChange'] },
                  { min: 3, message: '丹炉名称需在3-13个字符内' },
                ]}
                normalize={v => v.trim()}
              >
                <Input placeholder="请输入丹炉名称" showCount maxLength={13} />
              </MyForm.Item>
            </div>

            <div className="mb-6">
              <div className={styles.label}>选择镜像</div>
              <div className={styles.card}>
                <MyForm.Item name="image" rules={[{ required: true }]} noStyle>
                  <Radio.Group>
                    <div className="flex flex-col">
                      <Radio value={1} className={styles.customRadio}>
                        <div className="flex items-center font-semibold">
                          <div
                            className={classNames(
                              'w-4 h-4 border-4 border-solid transition-all rounded-full mr-2',
                              {
                                'border-primary': selectedImage === 1,
                                'border-neutral-500': selectedImage !== 1,
                              }
                            )}
                          ></div>
                          <div className="text-neutral-500 text-base font-semibold">
                            Stable Diffusion Web UI
                          </div>
                        </div>
                      </Radio>
                    </div>
                  </Radio.Group>
                </MyForm.Item>
                <div className="text-neutral-500 text-xs">
                  预装：Ubuntu20.04, Python 3.9, Pytorch1.12.1, CUDA 11.3, cuDNN
                  8, NVCC, VNC, Stable Diffusion web UI
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className={styles.label}>兑换GPU</div>
              <div className="rounded-2xl border-2 border-solid border-neutral-300">
                <div className="w-full text-neutral-500 p-4">
                  <div className="font-semibold text-[22px] leading-[32px]">
                    NVIDIA GeForce RTX 2080 Ti
                  </div>
                  <div className="mt-2 text-xs flex items-center">
                    <MyIcon icon="vc" className="mr-1 text-[16px]" />
                    <span>显存 11GB</span>
                  </div>
                  <div className="mt-2 text-xs flex items-center">
                    <MyIcon icon="microchip" className="mr-1 text-[16px]" />
                    <span>GPU 服务器专用</span>
                  </div>
                  <div className="mt-2 text-xs flex items-center">
                    <MyIcon icon="ram" className="mr-1 text-[16px]" />
                    <span>{'内存 ≥32GB'}</span>
                  </div>
                  <div className="mt-2 text-xs flex items-center">
                    <MyIcon icon="disk" className="mr-1 text-[16px]" />
                    <span>{'硬盘 ≥100GB'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className={styles.label}>兑换时长</div>
              <div className={styles.card}>0.5小时</div>
            </div>
          </MyForm>
        </div>

        <div
          className="bg-neutral-100 flex items-center justify-between absolute bottom-0 left-0 right-0 py-2.5 px-6"
          style={{
            boxShadow:
              '0px 1px 4px rgba(255, 255, 255, 0.08), 0px 0px 16px rgba(255, 255, 255, 0.08)',
          }}
        >
          <div className="flex items-center">
            <div className="text-neutral-500">
              <div className="text-[14px] leading-6 font-semibold">镜像：</div>
              <div className="text-sm">Stable Diffusion Web UI</div>
            </div>
            <div className="text-neutral-500 ml-4">
              <div className="text-[14px] leading-6 font-semibold">GPU：</div>
              <div className="text-sm">NVIDIA GeForce RTX 2080 Ti</div>
            </div>
          </div>
          <div>
            <MyBtn
              type="primary"
              className="!px-10"
              onClick={handleSubmit}
              loading={submitLoading}
            >
              确认
            </MyBtn>
          </div>
        </div>
      </section>
    </Drawer>
  )
}

export default memo(RedeemCodeDrawer, (prev, next) => {
  if (prev.visible !== next.visible) {
    return false
  }
  return true
})
